/* eslint-disable @typescript-eslint/no-unsafe-call */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import type { Dispatch } from '@reduxjs/toolkit';

import { type RootState } from '../../../services/serviceTypes';
import { STEP_NAMES } from '../constants/steps';
import type { CopilotState } from '../data-layer/types';
import {
  addCompletedStep,
  addMessage,
  addMessageToEntitlement,
  removeSkippedStep,
  setAccountPrompt,
  setConnectionPrompt,
  setParsedTemplates,
} from '../data-store/copilotSlice';
import { showError } from '../services/messageService';

import { createContinueSuccessMessage } from './buttonResponseMessageFactory';
import { buildConnectionPayload } from './connectionUtils';
import cleanupStepMessages from './messageCleanupUtils';
import { replacePlaceholders } from './placeholderUtils';
import isEditingPreviousStep from './previousStepDetectionUtils';
import STEP_CONFIGS from './stepConfigs';

interface StepHelpConfig {
  stepName: string;
  apiCall: (services: ApiServices, payload: unknown) => Promise<unknown>;
  payloadBuilder: (
    formData: Record<string, unknown>,
    state?: CopilotState
  ) => unknown;
  successCallback?: (
    dispatch: Dispatch,
    result: unknown,
    getState: () => unknown
  ) => void;
  errorCallback?: (dispatch: Dispatch, error: string) => void;
  [key: string]: unknown; // Allow additional properties
}

interface ApiServices {
  initiateOnboard?: (payload: unknown) => Promise<unknown>;
  testConnection?: (payload: unknown) => Promise<unknown>;
  saveUseCaseConfiguration?: (payload: unknown) => Promise<unknown>;
}

interface StepPromptConfig {
  stepName: string;
  apiCall: (services: ApiServices, payload: unknown) => Promise<unknown>;
  payloadBuilder: (
    formData: Record<string, unknown>,
    userPrompt: string,
    state?: CopilotState
  ) => unknown;
  successCallback?: (
    dispatch: Dispatch,
    result: unknown,
    getState: () => unknown
  ) => void;
  errorCallback?: (dispatch: Dispatch, error: string) => void;
  [key: string]: unknown;
}

export const handleStepHelpNo = async (
  dispatch: Dispatch,
  getState: () => unknown,
  services: ApiServices,
  config: StepHelpConfig
): Promise<void> => {
  const state = getState() as {
    copilot?: {
      navigation?: { activeStep?: string };
      ui?: { endpointKey?: string };
      endpointKey?: string;
    };
  };
  const activeStep = state.copilot?.navigation?.activeStep ?? '';
  const endpointKey =
    state.copilot?.ui?.endpointKey || state.copilot?.endpointKey;

  // Add "Skip for Now" response after helper message
  // Only clear connection prompt if skipping from Connection step
  if (activeStep === STEP_NAMES.CONNECTION) {
    dispatch(setConnectionPrompt(''));
  }

  dispatch(
    addMessage({
      id: `skip-response-${Date.now()}`,
      type: 'user_response',
      subType: 'userResponseNo',
      text: 'Skip for Now',
      timestamp: Date.now(),
    })
  );

  const copilotState = getState() as { copilot?: CopilotState };
  const formData = copilotState.copilot?.stepFormData ?? {};

  // Check if this is a previous step edit scenario
  const isPreviousStepEdit = isEditingPreviousStep(activeStep, getState);

  // Handle Connection step in edit mode
  if (activeStep === STEP_NAMES.CONNECTION && endpointKey) {
    const connectionData = formData.Connection ?? {};
    const generalData = formData.General ?? {};
    const applicationtype = String(
      generalData['securitysystems.systemtype'] || 'CUA'
    );

    const connectionPayload = buildConnectionPayload(
      connectionData as Record<string, unknown>,
      '@CUAPROMPT@'
    );

    try {
      const saveResult = await services.saveUseCaseConfiguration?.({
        applicationtype,
        endpointKey: Number(endpointKey),
        body: connectionPayload,
      });

      // Unwrap if it's a RTK Query mutation result
      if (
        saveResult &&
        typeof saveResult === 'object' &&
        'unwrap' in saveResult
      ) {
        await (saveResult as any).unwrap();
      }

      const testResult = await services.testConnection?.({
        endpointName: String(generalData['securitysystems.systemname'] || ''),
      });

      // Unwrap if it's a RTK Query mutation result
      if (
        testResult &&
        typeof testResult === 'object' &&
        'unwrap' in testResult
      ) {
        await (testResult as any).unwrap();
      }

      dispatch(addMessage(createContinueSuccessMessage()));
    } catch (error) {
      // Check if there was a previous prompt for CONNECTION step
      const stateonCatch = getState() as { copilot?: CopilotState };
      const existingPrompt = stateonCatch.copilot?.connectionPrompt || '';

      // Only clear if no previous prompt exists
      if (!existingPrompt) {
        dispatch(setConnectionPrompt(''));
      }

      showError(dispatch, 'Connection failed. Please try again.');
    }

    return;
  }

  try {
    const basePayload = config.payloadBuilder(formData, copilotState.copilot);

    const payload = {
      ...(basePayload as Record<string, unknown>),
      dispatch,
      getState,
    };

    const result = await config.apiCall(services, payload);

    dispatch(addCompletedStep(activeStep));
    dispatch(removeSkippedStep(activeStep));

    // Only show success message in previous step edit scenario
    if (isPreviousStepEdit) {
      dispatch(addMessage(createContinueSuccessMessage()));
    }

    if (config.successCallback) {
      config.successCallback(dispatch, result, getState);
    }
  } catch (error) {
    // Clear connection prompt on failure for CONNECTION step in handleStepHelpNo
    if (activeStep === STEP_NAMES.CONNECTION) {
      const stateOnCatch = getState() as { copilot?: CopilotState };
      const existingPrompt = stateOnCatch.copilot?.connectionPrompt || '';

      // Only clear if no previous prompt exists
      if (!existingPrompt) {
        dispatch(setConnectionPrompt(''));
      }
    }

    const errorMessage = `${config.stepName} failed. Please try again.`;

    if (config.errorCallback) {
      config.errorCallback(dispatch, errorMessage);
    } else {
      showError(dispatch, errorMessage);
    }
  }
};

export const handleEditNotAligningPrompt = async (
  dispatch: Dispatch,
  getState: () => unknown,
  services: { saveUseCaseConfiguration: any; testConnection?: any }
): Promise<void> => {
  const state = getState() as RootState;

  // Only clear connection prompt if this is for Connection step
  if (state.copilot.navigation.activeStep === STEP_NAMES.CONNECTION) {
    dispatch(setConnectionPrompt(''));
  }

  const stepName = 'Connection';
  const currentStepFormData = state.copilot.stepFormData[stepName] || {};
  const { endpointKey } = state.copilot.ui;
  const applicationtype =
    state.copilot.stepFormData.General['securitysystems.systemtype'] || 'CUA';

  if (stepName === STEP_NAMES.CONNECTION) {
    const connectionPayload = buildConnectionPayload(
      currentStepFormData,
      '@CUAPROMPT@'
    );

    try {
      await services
        .saveUseCaseConfiguration({
          applicationtype: String(applicationtype),
          endpointKey: Number(endpointKey),
          body: connectionPayload,
        })
        .unwrap();

      const generalData = state.copilot.stepFormData.General || {};

      await services.testConnection?.({
        endpointName: String(generalData['securitysystems.systemname'] || ''),
      });

      // Update parsedTemplates to clear the instructions
      const currentParsedTemplates = state.copilot.parsedTemplates || {};
      const currentConnection = currentParsedTemplates.Connection || [];
      const updatedConnection = Array.isArray(currentConnection)
        ? [...currentConnection]
        : [];

      (updatedConnection as any).instructions = '';

      dispatch(
        setParsedTemplates({
          ...currentParsedTemplates,
          Connection: updatedConnection,
        })
      );

      dispatch(
        addMessage({
          id: `success-${Date.now()}`,
          type: 'ai_response',
          subType: 'success',
          text: 'Connection details updated successfully.',
          timestamp: Date.now(),
        })
      );
    } catch (error) {
      dispatch(
        addMessage({
          id: `error-${Date.now()}`,
          type: 'ai_response',
          subType: 'error',
          timestamp: Date.now(),
        })
      );
    }
  }
};

export const handleStepHelpYes = async (
  dispatch: Dispatch,
  getState: () => unknown,
  services: ApiServices,
  config: StepHelpConfig,
  userPrompt: string
): Promise<void> => {
  const state = getState() as { copilot?: CopilotState };
  const formData = state.copilot?.stepFormData ?? {};

  try {
    // Get base payload from config
    const basePayload = config.payloadBuilder(formData, state.copilot);

    // For CONNECTION step, pass userPrompt separately, don't replace in payload
    if (config.stepName === 'Connection') {
      const payload = {
        ...(basePayload as Record<string, unknown>),
        dispatch,
        getState,
        userPrompt, // Add userPrompt for connection step
      };

      const result = await config.apiCall(services, payload);

      // Store connection prompt in Redux ONLY after successful API call
      dispatch(setConnectionPrompt(userPrompt));

      if (config.successCallback) {
        config.successCallback(dispatch, result, getState);
      }
    } else {
      // For other steps, replace @CUAPROMPT@ in payload
      const processedPayload = replacePlaceholders(
        basePayload as Record<string, unknown>,
        userPrompt
      );

      const payload = {
        ...processedPayload,
        dispatch,
        getState,
      };

      const result = await config.apiCall(services, payload);

      if (config.successCallback) {
        config.successCallback(dispatch, result, getState);
      }
    }
  } catch (error) {
    // Clean up messages between steps on failure
    if (config.stepName === 'Connection') {
      cleanupStepMessages(dispatch, 'Connection');
    }

    // Clear connection prompt on failure for CONNECTION step
    if (config.stepName === 'Connection') {
      const stateOnCatch = getState() as { copilot?: CopilotState };
      const existingPrompt = stateOnCatch.copilot?.connectionPrompt || '';

      // Only clear if no previous prompt exists
      if (!existingPrompt) {
        dispatch(setConnectionPrompt(''));
      }
    }

    const errorMessage = `${config.stepName} failed. Please try again.`;

    if (config.errorCallback) {
      config.errorCallback(dispatch, errorMessage);
    } else {
      showError(dispatch, errorMessage);
    }
  }
};

export const handleAccountHelpNo = async (
  dispatch: Dispatch,
  getState: () => unknown,
  services: ApiServices
): Promise<void> => {
  const state = getState() as {
    copilot?: { navigation?: { activeStep?: string } };
  };
  const activeStep = state.copilot?.navigation?.activeStep ?? 'CreateAccount';

  // Clear account prompt for "Skip for Now" - only for current account step
  const accountSteps = [
    'CreateAccount',
    'UpdateAccount',
    'EnableAccount',
    'DisableAccount',
    'RemoveAccount',
  ];

  if (accountSteps.includes(activeStep)) {
    dispatch(setAccountPrompt({ stepName: activeStep, prompt: '' }));
  }

  // Map activeStep to step config
  const stepConfigMap: Record<string, StepHelpConfig> = {
    CreateAccount: STEP_CONFIGS.CreateAccount as StepHelpConfig,
    UpdateAccount: STEP_CONFIGS.UpdateAccount as StepHelpConfig,
    EnableAccount: STEP_CONFIGS.EnableAccount as StepHelpConfig,
    DisableAccount: STEP_CONFIGS.DisableAccount as StepHelpConfig,
    RemoveAccount: STEP_CONFIGS.RemoveAccount as StepHelpConfig,
  };

  const config = stepConfigMap[activeStep] ?? stepConfigMap.CreateAccount;

  await handleStepHelpNo(dispatch, getState, services, config);
};

export const handleAccountHelpYes = async (
  dispatch: Dispatch,
  getState: () => unknown,
  services: ApiServices,
  userPrompt: string
): Promise<void> => {
  const state = getState() as {
    copilot?: {
      navigation?: { activeStep?: string };
      stepFormData?: Record<string, unknown>;
      endpointKey?: number;
    };
  };

  const activeStep = state.copilot?.navigation?.activeStep ?? 'CreateAccount';

  const formData = state.copilot?.stepFormData ?? {};
  const endpointKey = state.copilot?.endpointKey;

  try {
    if (services.saveUseCaseConfiguration && endpointKey) {
      // Get account data for the current step
      const accountData = (formData[activeStep] ?? {}) as Record<
        string,
        unknown
      >;

      // Replace @CUAPROMPT@ with user provided prompt in account data
      const processedAccountData = replacePlaceholders(accountData, userPrompt);

      // Create a copy and remove mappings from payload
      const cleanAccountData = { ...processedAccountData };

      delete cleanAccountData.mappings;

      const result = await services.saveUseCaseConfiguration({
        applicationtype: 'CUA',
        endpointKey,
        body: cleanAccountData,
      });

      // Check if result indicates failure
      if (result && typeof result === 'object') {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const resultObj = result as any;

        // Check for RTK Query error structure
        if (resultObj.error) {
          throw new Error('Save account configuration failed');
        }

        // Check for HTTP 500 status code in the result data
        if (
          resultObj.data &&
          typeof resultObj.data === 'object' &&
          resultObj.data.status === 500
        ) {
          throw new Error('API returned 500 status code');
        }

        // Check for other API error responses
        if (
          resultObj.status >= 400 ||
          (resultObj.data && resultObj.data.status >= 400)
        ) {
          throw new Error('Save account configuration failed');
        }
      }

      // Store account prompt in Redux ONLY after successful API call
      dispatch(setAccountPrompt({ stepName: activeStep, prompt: userPrompt }));

      // Handle success - same as handleStepHelpNo but with user prompt
      const stepConfigMap: Record<string, StepHelpConfig> = {
        CreateAccount: STEP_CONFIGS.CreateAccount as StepHelpConfig,
        UpdateAccount: STEP_CONFIGS.UpdateAccount as StepHelpConfig,
        EnableAccount: STEP_CONFIGS.EnableAccount as StepHelpConfig,
        DisableAccount: STEP_CONFIGS.DisableAccount as StepHelpConfig,
        RemoveAccount: STEP_CONFIGS.RemoveAccount as StepHelpConfig,
      };

      const config = stepConfigMap[activeStep] ?? stepConfigMap.CreateAccount;

      if (config.successCallback) {
        config.successCallback(dispatch, result, getState);
      }
    }
  } catch (error) {
    // Clear account prompt on failure - preserve existing if exists
    const stateOnCatch = getState() as { copilot?: CopilotState };
    const existingPrompt =
      stateOnCatch.copilot?.accountPrompts[activeStep] || '';

    // Only clear if no previous prompt exists
    if (!existingPrompt) {
      dispatch(setAccountPrompt({ stepName: activeStep, prompt: '' }));
    }

    const errorMessage = `${activeStep} failed. Please try again.`;

    showError(dispatch, errorMessage);
  }
};

export const handleStepPromptSubmission = async (
  dispatch: Dispatch,
  getState: () => unknown,
  services: ApiServices,
  config: StepPromptConfig,
  userPrompt: string
): Promise<void> => {
  const state = getState() as { copilot?: CopilotState };
  const formData = state.copilot?.stepFormData ?? {};

  try {
    // Handle different payloadBuilder signatures
    let basePayload;

    if (config.payloadBuilder.length === 3) {
      // CreateAccountPrompt style: (formData, userPrompt, state)
      basePayload = config.payloadBuilder(formData, userPrompt, state.copilot);
    } else {
      // Regular style: (formData, state) - cast to regular config type
      const regularConfig = config as unknown as StepHelpConfig;

      basePayload = regularConfig.payloadBuilder(formData, state.copilot);
    }

    const payload = {
      ...(basePayload as Record<string, unknown>),
      dispatch,
      getState,
    };

    const result = await config.apiCall(services, payload);

    if (config.successCallback) {
      config.successCallback(dispatch, result, getState);
    }
  } catch (error) {
    const errorMessage = `${config.stepName} failed. Please try again.`;

    if (config.errorCallback) {
      config.errorCallback(dispatch, errorMessage);
    } else {
      showError(dispatch, errorMessage);
    }
  }
};

export const handleFileInstructionsYes = (
  dispatch: Dispatch,
  getState: () => unknown,
  controller?: {
    handleUserPrompt?: (prompt: string) => void;
    addAccessController?: { handleUserPrompt?: (prompt: string) => void };
    removeAccessController?: { handleUserPrompt?: (prompt: string) => void };
  }
): void => {
  const state = getState() as RootState;

  // Get current entitlement context for proper message cleanup
  const currentIndex = state.copilot.entitlementIteration.currentIndex ?? 0;
  const removeAccessCurrentIndex =
    state.copilot.removeAccessEntitlementIndex ?? 0;
  const activeStep = state.copilot.navigation.activeStep ?? '';
  const isAddAccessFlow = activeStep.startsWith('AddAccess_');
  const isRemoveAccessFlow = activeStep.startsWith('RemoveAccess');

  // Use correct entitlement index based on flow type
  const entitlementIndex = isRemoveAccessFlow
    ? removeAccessCurrentIndex
    : currentIndex;

  // Add user's "Yes" response first with proper parent properties
  const userResponseMessage: {
    id: string;
    type: string;
    subType: string;
    text: string;
    timestamp: number;
    parentEntitlementIndex?: number;
    entitlementType?: string;
  } = {
    id: `user-yes-response-${Date.now()}`,
    type: 'user_response',
    subType: 'userResponseYes',
    text: 'Yes',
    timestamp: Date.now(),
  };

  // Add parent entitlement properties for proper cleanup
  if (isAddAccessFlow) {
    userResponseMessage.parentEntitlementIndex = entitlementIndex;
    userResponseMessage.entitlementType = 'addAccess';
  } else if (isRemoveAccessFlow) {
    userResponseMessage.parentEntitlementIndex = entitlementIndex;
    userResponseMessage.entitlementType = 'removeAccess';
  }

  if (isAddAccessFlow || isRemoveAccessFlow) {
    dispatch(
      addMessageToEntitlement({
        entitlementIndex,
        entitlementType: isAddAccessFlow ? 'addAccess' : 'removeAccess',
        message: userResponseMessage as Parameters<
          typeof addMessageToEntitlement
        >[0]['message'],
      })
    );
  } else {
    dispatch(
      addMessage(userResponseMessage as Parameters<typeof addMessage>[0])
    );
  }

  // Find the file extraction message to get the instructions
  const messages = state.copilot.messages || [];
  const fileExtractionMessage = messages.find(
    (msg) => msg.subType === 'fileExtraction' && 'text' in msg && msg.text
  );

  if (
    fileExtractionMessage &&
    'text' in fileExtractionMessage &&
    fileExtractionMessage.text
  ) {
    // Extract just the instructions part (remove introduction and confirmation question)
    const fullText = fileExtractionMessage.text;
    const lines = fullText.split('\n');

    // Remove first line ("Here I extracted instruction from your file.")
    // Remove last two lines (empty line + "Do the above instructions align with your expectations?")
    const instructionLines = lines.slice(1, -2);
    const extractedInstructions = instructionLines.join('\n').trim();

    if (!extractedInstructions) {
      return;
    }

    if (isAddAccessFlow) {
      if (controller?.addAccessController?.handleUserPrompt) {
        controller.addAccessController.handleUserPrompt(extractedInstructions);

        return;
      }
    } else if (isRemoveAccessFlow) {
      if (controller?.removeAccessController?.handleUserPrompt) {
        controller.removeAccessController.handleUserPrompt(
          extractedInstructions
        );

        return;
      }
    }

    // Fallback to generic controller handler if present
    if (controller?.handleUserPrompt) {
      controller.handleUserPrompt(extractedInstructions);
    }
  }
};

export const handleFileInstructionsNo = (
  dispatch: Dispatch,
  getState?: () => unknown
): void => {
  // Get entitlement context if available
  let currentIndex = 0;
  let removeAccessCurrentIndex = 0;
  let isAddAccessFlow = false;
  let isRemoveAccessFlow = false;

  if (getState) {
    const state = getState() as RootState;

    currentIndex = state.copilot.entitlementIteration.currentIndex ?? 0;
    removeAccessCurrentIndex = state.copilot.removeAccessEntitlementIndex ?? 0;
    const { activeStep } = state.copilot.navigation;

    isAddAccessFlow = activeStep.startsWith('AddAccess_') ?? false;
    isRemoveAccessFlow = activeStep.startsWith('RemoveAccess') ?? false;
  }

  // Use correct entitlement index based on flow type
  const entitlementIndex = isRemoveAccessFlow
    ? removeAccessCurrentIndex
    : currentIndex;

  // Create "No" response message
  const noResponseMessage = {
    id: Date.now().toString(),
    type: 'user_response' as const,
    subType: 'userResponseNo' as const,
    text: 'No',
    timestamp: Date.now(),
  };

  // Add "No" response with proper entitlement context
  if (isAddAccessFlow || isRemoveAccessFlow) {
    dispatch(
      addMessageToEntitlement({
        entitlementIndex,
        entitlementType: isAddAccessFlow ? 'addAccess' : 'removeAccess',
        message: noResponseMessage as Parameters<
          typeof addMessageToEntitlement
        >[0]['message'],
      })
    );
  } else {
    dispatch(addMessage(noResponseMessage as Parameters<typeof addMessage>[0]));
  }

  // Show the same three buttons as the first time (connection help)
  const retryButtonsMessage = {
    id: `connection-help-retry-${Date.now()}`,
    type: 'ai_response' as const,
    subType: 'selectOneOfOptions' as const,
    timestamp: Date.now(),
    buttons: [
      { label: 'Specify Prompt', action: 'connection_help_yes' },
      { label: 'Upload Browser Recording', action: 'connection_help_yes' },
      { label: 'Skip for Now', action: 'file_instruction_skip' },
    ],
  };

  // Add retry buttons with proper entitlement context
  if (isAddAccessFlow || isRemoveAccessFlow) {
    dispatch(
      addMessageToEntitlement({
        entitlementIndex,
        entitlementType: isAddAccessFlow ? 'addAccess' : 'removeAccess',
        message: retryButtonsMessage as Parameters<
          typeof addMessageToEntitlement
        >[0]['message'],
      })
    );
  } else {
    dispatch(
      addMessage(retryButtonsMessage as Parameters<typeof addMessage>[0])
    );
  }
};

export default handleStepHelpNo;
